/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

import AddIcon from '@mui/icons-material/esm/Add';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import { Badge, SvgIcon } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ConfirmDialog from '../../admin/ConfirmDialog';
import { withAppData } from '../../app/AppProvider';
import { runQuery } from '../../app/GraphQLWSClient';
import CustomTooltip from '../../utils/CustomTooltip';
import { makeUseFilterAndSort } from '../../utils/filterAndSort';
import SettingsPaper from '../../utils/SettingsPaper';
import TableSortHeader from '../../utils/TableSortHeader';
import DELETE_CONNECTION_MUTATION from './delete-connection.gql';

const useFilterAndSort = makeUseFilterAndSort(['name', 'type', 'lastModified'], 'name');

export const ConnectionTable = withAppData(({ connections }) => ({
	connections
}))(({ connections, onClose, onSelectConnection, onAddConnection }) => {
	const [confirmDelete, setConfirmDelete] = useState(null);
	const { values: sortedConnections, sort, setSortField } = useFilterAndSort(connections);
	const onDelete = async () => {
		try {
			const result = await runQuery(DELETE_CONNECTION_MUTATION, { connectionId: confirmDelete.id });
			if (!result.success) {
				console.error(result.message);
			}
		} catch (e) {
			console.error(e);
		} finally {
			setConfirmDelete(null);
		}
	};

	return (
		<SettingsPaper
			helpContext="connectionsettings"
			open
			close={() => onClose()}
			style={{ width: '600px' }}
			title={<FormattedMessage id="ConnectionTable.Title" defaultMessage="Connections" />}
		>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
				<CustomTooltip header="Tooltip.AddConnectionTitle" message="Tooltip.AddConnectionMessage">
					<IconButton aria-label="Add" onClick={onAddConnection} size="small">
						<AddIcon />
					</IconButton>
				</CustomTooltip>
			</Box>
			<ConfirmDialog
				open={!!confirmDelete}
				onConfirm={onDelete}
				title={<FormattedMessage id="ConnectionView.DeleteConnection" defaultMessage="Delete Connection" />}
				content={
					<FormattedMessage
						id="ConnectionView.DeleteConnectionContent"
						defaultMessage=""
						values={{
							name: confirmDelete ? confirmDelete.name : ''
						}}
					/>
				}
				onCancel={() => setConfirmDelete(null)}
			/>
			<TableContainer sx={{ height: '100%' }}>
				<Table stickyHeader size="medium" aria-label="collapsible table">
					<TableSortHeader
						height={48}
						cells={[
							{
								id: 'gap',
								hideOnSmallDisplays: true,
								numeric: false,
								padding: false,
								sort: false,
								label: '',
								width: '1%',
								minWidth: '16px'
							},
							{ id: 'name', numeric: false, padding: false, label: 'Name', width: '40%' },
							{ id: 'type', numeric: false, padding: false, label: 'Source', width: '34%' },
							{
								id: 'lastModified',
								hideOnSmallDisplays: true,
								numeric: false,
								label: 'LastModified',
								width: '25%'
							},
							{
								id: 'delete',
								numeric: false,
								sort: false,
								padding: false,
								// label: 'AppsPage.Actions',
								width: '15%',
								minWidth: '40px'
							}
						]}
						orderBy={sort.field}
						order={sort.direction}
						onRequestSort={(event, property) => setSortField(property)}
						// onFieldToggle={(field, state) => this.onFieldToggle(field, state)}
					/>
					<TableBody>
						{sortedConnections.map((connection) => (
							<TableRow
								style={{
									height: '40px',
									cursor: 'pointer'
								}}
								hover
								onClick={() => onSelectConnection(connection.id)}
								tabIndex={-1}
								key={`${connection.id}`}
							>
								<TableCell
									// className={classes.hideOnSmallDisplays}
									// onClick={() => handleSelection(resource)}
									padding="none"
								>
									{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
									<div
										// onClick={() => handleSelection(resource)}
										style={{
											margin: '9px'
										}}
									>
										<Badge
											color="error"
											variant="dot"
											invisible={!connection.status || connection.status.isConnected}
										>
											<IconButton style={{ padding: '4px' }} size="small">
												<SvgIcon>
													<path d="M21.4 7.5C22.2 8.3 22.2 9.6 21.4 10.3L18.6 13.1L10.8 5.3L13.6 2.5C14.4 1.7 15.7 1.7 16.4 2.5L18.2 4.3L21.2 1.3L22.6 2.7L19.6 5.7L21.4 7.5M15.6 13.3L14.2 11.9L11.4 14.7L9.3 12.6L12.1 9.8L10.7 8.4L7.9 11.2L6.4 9.8L3.6 12.6C2.8 13.4 2.8 14.7 3.6 15.4L5.4 17.2L1.4 21.2L2.8 22.6L6.8 18.6L8.6 20.4C9.4 21.2 10.7 21.2 11.4 20.4L14.2 17.6L12.8 16.2L15.6 13.3Z" />
												</SvgIcon>
											</IconButton>
										</Badge>
									</div>
								</TableCell>
								<TableCell
									style={{ fontWeight: 'bold' }}
									// onClick={() => handleSelection(resource)}
									padding="none"
									component="th"
									scope="row"
								>
									{connection.name}
								</TableCell>
								<TableCell
									// className={classes.hideOnSmallDisplays}
									// onClick={() => handleSelection(resource)}
									padding="none"
								>
									{connection.type}
								</TableCell>
								<TableCell
									// className={classes.hideOnSmallDisplays}
									// onClick={() => handleSelection(resource)}
									padding="none"
								>
									{formatDistanceToNow(new Date(connection.lastModified), { addSuffix: true })}
								</TableCell>
								<TableCell
									// className={classes.hideOnSmallDisplays}
									// onClick={() => handleSelection(resource)}
									padding="none"
								>
									<IconButton
										// style={{ color: '#FF0022' }}
										onClick={(event) => {
											event.stopPropagation();
											setConfirmDelete(connection);
										}}
										size="large"
									>
										<DeleteIcon />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{/* </Box> */}
		</SettingsPaper>
	);
});
